import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, Plus, Bell, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";

const emptyProduct = { name: "", description: "", price: "", images: "", category: "Dresses",
                       sizes: "", colors: "", tags: "", stock: 100 };

export default function VendorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [np, setNp] = useState(emptyProduct);
  const [tab, setTab] = useState("products");
  const [connect, setConnect] = useState(null);
  const [connectBusy, setConnectBusy] = useState(false);

  const load = async () => {
    try { const { data } = await client.get("/vendor/me"); setData(data); }
    catch (err) {
      if (err.response?.status === 404) setError("no_vendor");
      else if (err.response?.status === 401) setError("no_auth");
      else setError("unknown");
    }
  };

  const loadConnect = async () => {
    try { const { data } = await client.get("/vendor/connect/status"); setConnect(data); }
    catch { setConnect({ connected: false, charges_enabled: false, payouts_enabled: false }); }
  };

  useEffect(() => { if (user) { load(); loadConnect(); } }, [user]);

  const startConnect = async () => {
    setConnectBusy(true);
    try {
      const { data } = await client.post("/vendor/connect/onboard", {
        return_url: window.location.href,
        refresh_url: window.location.href,
      });
      if (data.onboarding_url) window.location.href = data.onboarding_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start Stripe onboarding");
    }
    setConnectBusy(false);
  };

  const notifyRestock = async (pid) => {
    try {
      const { data } = await client.post(`/vendor/products/${pid}/notify-restock`);
      const n = data.total_subscribers;
      if (n === 0) { toast.info("No wishlist subscribers for this product yet"); return; }
      toast.success(`${n} shopper${n === 1 ? "" : "s"} notified${data.mocked ? " (mocked — Resend key not set)" : ""}`);
    } catch { toast.error("Could not send restock alerts"); }
  };

  if (!user) return (
    <div className="container-editorial mt-14 py-20 text-center">
      <h1 className="font-serif text-4xl tracking-tighter">Sign in to view your dashboard.</h1>
      <button onClick={() => navigate("/login?redirect=/vendor/dashboard")} className="btn-terracotta mt-6">Sign In →</button>
    </div>
  );

  if (error === "no_vendor") return (
    <div className="container-editorial mt-14 py-20 text-center">
      <p className="label-uppercase text-[#B24C38]">Not yet a vendor</p>
      <h1 className="font-serif text-4xl tracking-tighter mt-3">Open your storefront first.</h1>
      <Link to="/vendor/apply" className="btn-terracotta mt-6 inline-flex">Apply now →</Link>
    </div>
  );

  if (!data) return <div className="container-editorial py-24"><p className="label-uppercase">Loading…</p></div>;
  const { vendor, products, orders } = data;

  const submitProduct = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: np.name, description: np.description, price: parseFloat(np.price),
        category: np.category, stock: parseInt(np.stock, 10) || 100,
        images: np.images.split(/[\n,]+/).map(s => s.trim()).filter(Boolean),
        sizes: np.sizes.split(",").map(s => s.trim()).filter(Boolean),
        colors: np.colors.split(",").map(s => s.trim()).filter(Boolean),
        tags: np.tags.split(",").map(s => s.trim()).filter(Boolean),
      };
      await client.post("/vendor/products", payload);
      toast.success("Product added");
      setOpenNew(false); setNp(emptyProduct); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to add"); }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Remove this product?")) return;
    try { await client.delete(`/vendor/products/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };

  const revenue = orders.reduce((s, o) => s + (o.vendor_total || 0), 0);

  return (
    <div className="container-editorial mt-8 md:mt-14">
      <div className="border-b border-[#EAE5D9] pb-10 mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="label-uppercase text-[#B24C38]">Vendor dashboard</p>
          <h1 className="font-serif text-5xl md:text-6xl tracking-tighter mt-3" data-testid="vendor-dashboard-name">{vendor.name}</h1>
          <p className="text-[#4A4A4A] mt-2">{vendor.tagline}</p>
        </div>
        <Link to={`/vendor/${vendor.slug}`} className="btn-outline-ink">View public page →</Link>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="border border-[#EAE5D9] p-6">
          <p className="label-uppercase text-[#4A4A4A]">Products</p>
          <p className="font-serif text-4xl mt-2" data-testid="vendor-stat-products">{products.length}</p>
        </div>
        <div className="border border-[#EAE5D9] p-6">
          <p className="label-uppercase text-[#4A4A4A]">Orders</p>
          <p className="font-serif text-4xl mt-2" data-testid="vendor-stat-orders">{orders.length}</p>
        </div>
        <div className="border border-[#EAE5D9] p-6">
          <p className="label-uppercase text-[#4A4A4A]">Revenue</p>
          <p className="font-serif text-4xl mt-2" data-testid="vendor-stat-revenue">${revenue.toFixed(0)}</p>
        </div>
      </div>

      {/* Stripe Connect payout banner */}
      <div className="border border-[#111] bg-[#1A1A1A] text-[#F7F5F0] p-6 md:p-8 mb-12 flex flex-wrap justify-between items-center gap-4" data-testid="vendor-connect-card">
        <div className="flex-1 min-w-[260px]">
          <p className="label-uppercase text-[#D4A373]">Payouts · Stripe Connect</p>
          {connect?.charges_enabled && connect?.payouts_enabled ? (
            <>
              <p className="font-serif text-2xl mt-2 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-[#7EBA6C]"/>
                You're set up to receive payouts.
              </p>
              <p className="text-sm text-[#c9c2b0] mt-1">
                Platform fee: {connect.platform_fee_pct}% · Payouts land in your bank on Stripe's schedule.
              </p>
            </>
          ) : connect?.connected ? (
            <>
              <p className="font-serif text-2xl mt-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#D4A373]"/>
                Finish your Stripe onboarding
              </p>
              <p className="text-sm text-[#c9c2b0] mt-1">
                Complete the Stripe form to unlock automatic payouts on every sale.
              </p>
            </>
          ) : (
            <>
              <p className="font-serif text-2xl mt-2">Turn on automatic payouts.</p>
              <p className="text-sm text-[#c9c2b0] mt-1">
                Connect your bank via Stripe Express and receive your 90% share on every sale automatically.
              </p>
            </>
          )}
        </div>
        {!(connect?.charges_enabled && connect?.payouts_enabled) && (
          <button onClick={startConnect} disabled={connectBusy}
                  data-testid="vendor-connect-start"
                  className="bg-[#B24C38] text-white px-6 py-3 text-xs tracking-widest uppercase font-semibold flex items-center gap-2 hover:bg-[#9c4030]">
            {connectBusy ? "Redirecting…" : (connect?.connected ? "Continue onboarding" : "Connect Stripe")}
            <ExternalLink className="w-4 h-4"/>
          </button>
        )}
      </div>

      <div className="flex gap-6 border-b border-[#EAE5D9] mb-8">
        {["products", "orders"].map(t => (
          <button key={t} onClick={() => setTab(t)} data-testid={`vendor-tab-${t}`}
                  className={`label-uppercase pb-4 ${tab===t ? "border-b-2 border-[#B24C38] text-[#B24C38]" : "text-[#4A4A4A]"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "products" && (
        <div>
          <div className="flex justify-end mb-6">
            <Dialog open={openNew} onOpenChange={setOpenNew}>
              <DialogTrigger asChild>
                <button className="btn-terracotta" data-testid="vendor-add-product-btn"><Plus className="w-4 h-4"/> Add product</button>
              </DialogTrigger>
              <DialogContent className="bg-[#F7F5F0] rounded-none border border-[#111] max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-serif text-3xl tracking-tighter">New product</DialogTitle></DialogHeader>
                <form onSubmit={submitProduct} className="space-y-4">
                  {[
                    ["name","Name"],["price","Price (USD)","number"],["category","Category"],
                    ["sizes","Sizes (comma separated)"],["colors","Colors (comma separated)"],
                    ["tags","Tags (comma separated)"],["stock","Stock","number"],
                  ].map(([k, label, type]) => (
                    <div key={k}>
                      <label className="label-uppercase block mb-1">{label}</label>
                      <Input required={k==="name"||k==="price"} type={type||"text"} value={np[k]}
                             onChange={e => setNp(p => ({...p, [k]: e.target.value}))}
                             data-testid={`vp-${k}`}
                             className="rounded-none border-[#111] bg-transparent"/>
                    </div>
                  ))}
                  <div>
                    <label className="label-uppercase block mb-1">Image URLs (one per line)</label>
                    <Textarea required rows={3} value={np.images} onChange={e => setNp(p => ({...p, images: e.target.value}))}
                              data-testid="vp-images"
                              className="rounded-none border-[#111] bg-transparent"/>
                  </div>
                  <div>
                    <label className="label-uppercase block mb-1">Description</label>
                    <Textarea required rows={4} value={np.description} onChange={e => setNp(p => ({...p, description: e.target.value}))}
                              data-testid="vp-description"
                              className="rounded-none border-[#111] bg-transparent"/>
                  </div>
                  <button type="submit" className="btn-terracotta w-full justify-center" data-testid="vp-submit">Save product</button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          <table className="w-full text-sm">
            <thead className="border-b border-[#EAE5D9]">
              <tr>
                <th className="text-left py-3 label-uppercase text-[#4A4A4A]">Product</th>
                <th className="text-right py-3 label-uppercase text-[#4A4A4A]">Price</th>
                <th className="text-right py-3 label-uppercase text-[#4A4A4A]">Stock</th>
                <th className="text-right py-3 label-uppercase text-[#4A4A4A]"></th>
              </tr>
            </thead>
            <tbody>
              {products.map(p => (
                <tr key={p.id} className="border-b border-[#EAE5D9]" data-testid={`vendor-product-row-${p.id}`}>
                  <td className="py-4">
                    <div className="flex items-center gap-4">
                      <img src={p.images?.[0]} alt="" className="w-14 h-16 object-cover"/>
                      <div>
                        <p className="font-serif text-lg">{p.name}</p>
                        <p className="text-xs text-[#4A4A4A]">{p.category}</p>
                      </div>
                    </div>
                  </td>
                  <td className="text-right">${p.price.toFixed(2)}</td>
                  <td className="text-right">{p.stock}</td>
                  <td className="text-right">
                    <button onClick={() => notifyRestock(p.id)} data-testid={`vendor-notify-${p.id}`}
                            className="p-2 hover:text-[#2C402E]" title="Notify wishlist subscribers"><Bell className="w-4 h-4"/></button>
                    <button onClick={() => deleteProduct(p.id)} data-testid={`vendor-delete-${p.id}`}
                            className="p-2 hover:text-[#B24C38]"><Trash2 className="w-4 h-4"/></button>
                  </td>
                </tr>
              ))}
              {products.length === 0 && (
                <tr><td colSpan={4} className="py-12 text-center text-[#4A4A4A]">No products yet — add your first piece.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-6">
          {orders.length === 0 && <p className="text-[#4A4A4A] py-12 text-center">No orders yet.</p>}
          {orders.map(o => (
            <div key={o.id} className="border border-[#EAE5D9] p-6" data-testid={`vendor-order-${o.id}`}>
              <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
                <div>
                  <p className="label-uppercase text-[#4A4A4A]">Order · {o.id.slice(0,8)}</p>
                  <p className="font-serif text-2xl mt-1">${o.vendor_total.toFixed(2)}</p>
                </div>
                <div className="text-right">
                  <span className="label-uppercase text-[#2C402E]">{o.status}</span>
                  {o.shabbat_hold && (
                    <p className="label-uppercase text-[#B24C38] mt-1">Shabbat hold</p>
                  )}
                  <p className="text-xs text-[#4A4A4A] mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <ul className="text-sm space-y-1">
                {o.vendor_items.map((it, i) => (
                  <li key={i}>· {it.quantity} × {it.name} {it.size ? `(${it.size})` : ""}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
